import React, { useState } from 'react';
import {
  AgentDecision,
  AIAgent,
  AgentTask,
  AgentAction
} from '../../../types/aiAgent';
import { clickable } from "components/ui/interactive";
import { SelectField } from "components/ui/select-field";

interface ReasoningStep {
  id: string;
  timestamp: Date;
  type: 'analysis' | 'data_gathering' | 'evaluation' | 'decision' | 'validation';
  title: string;
  description: string;
  inputs: any[];
  outputs: any[];
  confidence: number;
  duration: number; // in seconds
  dependencies: string[];
}

interface AgentReasoningTrailProps {
  decision: AgentDecision;
  agent: AIAgent;
  task?: AgentTask;
  actions?: AgentAction[];
  onStepSelect?: (step: ReasoningStep) => void;
}

export const AgentReasoningTrail: React.FC<AgentReasoningTrailProps> = ({
  decision,
  agent,
  task: _task,
  actions: _actions,
  onStepSelect
}) => {
  const [selectedStep, setSelectedStep] = useState<ReasoningStep | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'timeline' | 'tree' | 'detailed'>('timeline');

  // Generate mock reasoning steps based on decision type
  const generateReasoningSteps = (): ReasoningStep[] => {
    const baseSteps: ReasoningStep[] = [
      {
        id: 'step_1',
        timestamp: new Date(decision.timestamp.getTime() - 300000), // 5 minutes before
        type: 'data_gathering',
        title: 'Data Collection',
        description: 'Gathered relevant data for decision analysis',
        inputs: ['historical_performance', 'current_metrics', 'user_behavior'],
        outputs: ['aggregated_data', 'data_quality_score'],
        confidence: 0.92,
        duration: 45,
        dependencies: []
      },
      {
        id: 'step_2',
        timestamp: new Date(decision.timestamp.getTime() - 240000), // 4 minutes before
        type: 'analysis',
        title: 'Statistical Analysis',
        description: 'Performed statistical analysis on collected data',
        inputs: ['aggregated_data', 'statistical_models'],
        outputs: ['analysis_results', 'significance_tests'],
        confidence: 0.88,
        duration: 60,
        dependencies: ['step_1']
      },
      {
        id: 'step_3',
        timestamp: new Date(decision.timestamp.getTime() - 180000), // 3 minutes before
        type: 'evaluation',
        title: 'Alternative Evaluation',
        description: 'Evaluated alternative approaches and their potential outcomes',
        inputs: ['analysis_results', 'business_constraints', 'risk_parameters'],
        outputs: ['alternative_rankings', 'risk_assessments'],
        confidence: 0.85,
        duration: 90,
        dependencies: ['step_2']
      },
      {
        id: 'step_4',
        timestamp: new Date(decision.timestamp.getTime() - 60000), // 1 minute before
        type: 'validation',
        title: 'Decision Validation',
        description: 'Validated decision against business rules and constraints',
        inputs: ['alternative_rankings', 'business_rules', 'approval_thresholds'],
        outputs: ['validation_results', 'compliance_check'],
        confidence: 0.91,
        duration: 30,
        dependencies: ['step_3']
      },
      {
        id: 'step_5',
        timestamp: decision.timestamp,
        type: 'decision',
        title: 'Final Decision',
        description: decision.explanation.summary,
        inputs: ['validation_results', 'final_parameters'],
        outputs: ['decision_output', 'implementation_plan'],
        confidence: decision.confidence,
        duration: 15,
        dependencies: ['step_4']
      }
    ];

    return baseSteps;
  };

  const reasoningSteps = generateReasoningSteps();

  const getStepIcon = (type: ReasoningStep['type']) => {
    const icons = {
      analysis: '🔍',
      data_gathering: '📊',
      evaluation: '⚖️',
      decision: '🎯',
      validation: '✅'
    };
    return icons[type];
  };

  const getStepColor = (type: ReasoningStep['type']) => {
    const colors = {
      analysis: 'border-blue-300 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/30',
      data_gathering: 'border-green-300 dark:border-green-800/50 bg-green-50 dark:bg-green-900/30',
      evaluation: 'border-yellow-300 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-900/30',
      decision: 'border-purple-300 dark:border-purple-800/50 bg-purple-50 dark:bg-purple-900/30',
      validation: 'border-indigo-300 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-900/30'
    };
    return colors[type];
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (confidence >= 0.8) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (confidence >= 0.7) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const toggleStepExpansion = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const handleStepClick = (step: ReasoningStep) => {
    setSelectedStep(step);
    onStepSelect?.(step);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">Reasoning Trail</h3>
            <p className="text-sm text-content-secondary mt-1">
              Step-by-step decision process by {agent.name}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <SelectField
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as any)}
              className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="timeline">Timeline View</option>
              <option value="tree">Tree View</option>
              <option value="detailed">Detailed View</option>
            </SelectField>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Total Steps</div>
            <div className="text-lg font-semibold text-content">{reasoningSteps.length}</div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Total Duration</div>
            <div className="text-lg font-semibold text-content">
              {formatDuration(reasoningSteps.reduce((sum, step) => sum + step.duration, 0))}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Avg Confidence</div>
            <div className="text-lg font-semibold text-content">
              {Math.round(reasoningSteps.reduce((sum, step) => sum + step.confidence, 0) / reasoningSteps.length * 100)}%
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Decision Type</div>
            <div className="text-lg font-semibold text-content capitalize">
              {decision.type.replace('_', ' ')}
            </div>
          </div>
        </div>
      </div>

      {/* Reasoning Steps */}
      <div className="p-6">
        {viewMode === 'timeline' && (
          <TimelineView
            steps={reasoningSteps}
            expandedSteps={expandedSteps}
            selectedStep={selectedStep}
            onStepClick={handleStepClick}
            onToggleExpansion={toggleStepExpansion}
            getStepIcon={getStepIcon}
            getStepColor={getStepColor}
            getConfidenceColor={getConfidenceColor}
            formatDuration={formatDuration}
          />
        )}

        {viewMode === 'tree' && (
          <TreeView
            steps={reasoningSteps}
            expandedSteps={expandedSteps}
            selectedStep={selectedStep}
            onStepClick={handleStepClick}
            onToggleExpansion={toggleStepExpansion}
            getStepIcon={getStepIcon}
            getStepColor={getStepColor}
            getConfidenceColor={getConfidenceColor}
            formatDuration={formatDuration}
          />
        )}

        {viewMode === 'detailed' && (
          <DetailedView
            steps={reasoningSteps}
            selectedStep={selectedStep}
            onStepClick={handleStepClick}
            getStepIcon={getStepIcon}
            getStepColor={getStepColor}
            getConfidenceColor={getConfidenceColor}
            formatDuration={formatDuration}
          />
        )}
      </div>

      {/* Step Detail Panel */}
      {selectedStep && (
        <StepDetailPanel
          step={selectedStep}
          onClose={() => setSelectedStep(null)}
          getStepIcon={getStepIcon}
          getConfidenceColor={getConfidenceColor}
          formatDuration={formatDuration}
        />
      )}
    </div>
  );
};

// Timeline View Component
const TimelineView: React.FC<{
  steps: ReasoningStep[];
  expandedSteps: Set<string>;
  selectedStep: ReasoningStep | null;
  onStepClick: (step: ReasoningStep) => void;
  onToggleExpansion: (stepId: string) => void;
  getStepIcon: (type: ReasoningStep['type']) => string;
  getStepColor: (type: ReasoningStep['type']) => string;
  getConfidenceColor: (confidence: number) => string;
  formatDuration: (seconds: number) => string;
}> = ({
  steps,
  expandedSteps,
  selectedStep,
  onStepClick,
  onToggleExpansion,
  getStepIcon,
  getStepColor,
  getConfidenceColor,
  formatDuration
}) => {
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div key={step.id} className="relative">
          {/* Timeline connector */}
          {index < steps.length - 1 && (
            <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-300"></div>
          )}
          
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedStep?.id === step.id
                ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-800/50'
                : getStepColor(step.type)
            }`}
            onClick={() => onStepClick(step)}
          >
            <div className="flex items-start space-x-4">
              {/* Step Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-surface rounded-full border-2 border-line-strong flex items-center justify-center text-xl">
                {getStepIcon(step.type)}
              </div>
              
              {/* Step Content */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-content">{step.title}</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(step.confidence)}`}>
                      {Math.round(step.confidence * 100)}%
                    </span>
                    <span className="text-xs text-content-muted">
                      {formatDuration(step.duration)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpansion(step.id);
                      }}
                      className="text-content-subtle hover:text-content-secondary"
                    >
                      {expandedSteps.has(step.id) ? '▼' : '▶'}
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{step.description}</p>
                
                <div className="text-xs text-content-muted">
                  {step.timestamp.toLocaleTimeString()}
                </div>

                {/* Expanded Details */}
                {expandedSteps.has(step.id) && (
                  <div className="mt-4 pt-4 border-t border-line">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-medium text-content mb-2">Inputs</h5>
                        <ul className="space-y-1">
                          {step.inputs.map((input, i) => (
                            <li key={i} className="text-sm text-content-secondary flex items-center space-x-2">
                              <span className="text-blue-600 dark:text-blue-300">→</span>
                              <span>{typeof input === 'string' ? input : JSON.stringify(input)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-content mb-2">Outputs</h5>
                        <ul className="space-y-1">
                          {step.outputs.map((output, i) => (
                            <li key={i} className="text-sm text-content-secondary flex items-center space-x-2">
                              <span className="text-green-600 dark:text-green-300">←</span>
                              <span>{typeof output === 'string' ? output : JSON.stringify(output)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {step.dependencies.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-medium text-content mb-2">Dependencies</h5>
                        <div className="flex flex-wrap gap-2">
                          {step.dependencies.map(dep => (
                            <span key={dep} className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                              {dep}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Tree View Component
const TreeView: React.FC<{
  steps: ReasoningStep[];
  expandedSteps: Set<string>;
  selectedStep: ReasoningStep | null;
  onStepClick: (step: ReasoningStep) => void;
  onToggleExpansion: (stepId: string) => void;
  getStepIcon: (type: ReasoningStep['type']) => string;
  getStepColor: (type: ReasoningStep['type']) => string;
  getConfidenceColor: (confidence: number) => string;
  formatDuration: (seconds: number) => string;
}> = ({
  steps,
  selectedStep,
  onStepClick,
  getStepIcon,
  getStepColor,
  getConfidenceColor,
  formatDuration
}) => {
  // Build dependency tree
  const buildTree = (steps: ReasoningStep[]) => {
    const stepMap = new Map(steps.map(step => [step.id, step]));
    const roots: ReasoningStep[] = [];
    const children = new Map<string, ReasoningStep[]>();

    steps.forEach(step => {
      if (step.dependencies.length === 0) {
        roots.push(step);
      } else {
        step.dependencies.forEach(depId => {
          if (!children.has(depId)) {
            children.set(depId, []);
          }
          children.get(depId)!.push(step);
        });
      }
    });

    return { roots, children, stepMap };
  };

  const { roots, children } = buildTree(steps);

  const renderTreeNode = (step: ReasoningStep, level: number = 0) => {
    const nodeChildren = children.get(step.id) || [];
    
    return (
      <div key={step.id} className="space-y-2">
        <div
          className={`border rounded-lg p-3 cursor-pointer transition-all ${
            selectedStep?.id === step.id
              ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-800/50'
              : getStepColor(step.type)
          }`}
          style={{ marginLeft: `${level * 24}px` }}
          {...clickable(() => onStepClick(step))}
        >
          <div className="flex items-center space-x-3">
            <div className="text-lg">{getStepIcon(step.type)}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-content">{step.title}</h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(step.confidence)}`}>
                    {Math.round(step.confidence * 100)}%
                  </span>
                  <span className="text-xs text-content-muted">
                    {formatDuration(step.duration)}
                  </span>
                </div>
              </div>
              <p className="text-sm text-content-secondary mt-1">{step.description}</p>
            </div>
          </div>
        </div>
        
        {nodeChildren.map(child => renderTreeNode(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {roots.map(root => renderTreeNode(root))}
    </div>
  );
};

// Detailed View Component
const DetailedView: React.FC<{
  steps: ReasoningStep[];
  selectedStep: ReasoningStep | null;
  onStepClick: (step: ReasoningStep) => void;
  getStepIcon: (type: ReasoningStep['type']) => string;
  getStepColor: (type: ReasoningStep['type']) => string;
  getConfidenceColor: (confidence: number) => string;
  formatDuration: (seconds: number) => string;
}> = ({
  steps,
  selectedStep,
  onStepClick,
  getStepIcon,
  getStepColor,
  getConfidenceColor,
  formatDuration
}) => {
  return (
    <div className="space-y-6">
      {steps.map(step => (
        <div
          key={step.id}
          className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
            selectedStep?.id === step.id
              ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-800/50'
              : getStepColor(step.type)
          }`}
          {...clickable(() => onStepClick(step))}
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-16 h-16 bg-surface rounded-full border-2 border-line-strong flex items-center justify-center text-2xl">
              {getStepIcon(step.type)}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-content">{step.title}</h4>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(step.confidence)}`}>
                    {Math.round(step.confidence * 100)}% Confidence
                  </span>
                  <span className="text-sm text-content-muted">
                    Duration: {formatDuration(step.duration)}
                  </span>
                </div>
              </div>
              
              <p className="text-gray-700 dark:text-gray-300 mb-4">{step.description}</p>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-content mb-3">Inputs</h5>
                  <div className="space-y-2">
                    {step.inputs.map((input, i) => (
                      <div key={i} className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                        <div className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                          Input {i + 1}
                        </div>
                        <div className="text-sm text-blue-800 dark:text-blue-300">
                          {typeof input === 'string' ? input : JSON.stringify(input, null, 2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-content mb-3">Outputs</h5>
                  <div className="space-y-2">
                    {step.outputs.map((output, i) => (
                      <div key={i} className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                        <div className="text-sm font-medium text-green-900 dark:text-green-300 mb-1">
                          Output {i + 1}
                        </div>
                        <div className="text-sm text-green-800 dark:text-green-300">
                          {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-line">
                <div className="flex items-center justify-between text-sm text-content-secondary">
                  <span>Executed at: {step.timestamp.toLocaleString()}</span>
                  {step.dependencies.length > 0 && (
                    <span>Dependencies: {step.dependencies.join(', ')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Step Detail Panel
const StepDetailPanel: React.FC<{
  step: ReasoningStep;
  onClose: () => void;
  getStepIcon: (type: ReasoningStep['type']) => string;
  getConfidenceColor: (confidence: number) => string;
  formatDuration: (seconds: number) => string;
}> = ({ step, onClose, getStepIcon, getConfidenceColor, formatDuration }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{getStepIcon(step.type)}</div>
            <div>
              <h3 className="text-lg font-semibold text-content">{step.title}</h3>
              <p className="text-sm text-content-secondary">Step Details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-content-subtle hover:text-content-secondary"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-content mb-2">Description</h4>
              <p className="text-gray-700 dark:text-gray-300">{step.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="text-sm text-content-secondary">Confidence</div>
                <div className={`font-semibold ${getConfidenceColor(step.confidence).split(' ')[0]}`}>
                  {Math.round(step.confidence * 100)}%
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="text-sm text-content-secondary">Duration</div>
                <div className="font-semibold text-content">
                  {formatDuration(step.duration)}
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-content mb-3">Inputs & Outputs</h4>
              <div className="space-y-4">
                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Inputs</h5>
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                    <pre className="text-sm text-blue-800 dark:text-blue-300 whitespace-pre-wrap">
                      {JSON.stringify(step.inputs, null, 2)}
                    </pre>
                  </div>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Outputs</h5>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                    <pre className="text-sm text-green-800 dark:text-green-300 whitespace-pre-wrap">
                      {JSON.stringify(step.outputs, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-content mb-2">Metadata</h4>
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-content-secondary">Timestamp:</span>
                    <span className="ml-2 font-medium">{step.timestamp.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-content-secondary">Type:</span>
                    <span className="ml-2 font-medium capitalize">{step.type.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-content-secondary">Dependencies:</span>
                    <span className="ml-2 font-medium">
                      {step.dependencies.length > 0 ? step.dependencies.join(', ') : 'None'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};